import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext, API } from '@/App';
import {
  X, Building2, MapPin, Users, ClipboardList, ShieldCheck, Boxes, Leaf, Lock,
  Wrench, AlertTriangle, History, Printer, Download, FileText, ChevronDown, ChevronRight, Loader2,
  MessageSquarePlus, Check, CornerUpLeft, Ban, Clock,
} from 'lucide-react';

const fmt = (v) => {
  if (!v) return '—';
  const s = String(v).slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  return m ? `${m[2]}/${m[3]}/${m[1].slice(2)}` : String(v);
};

const Pill = ({ s }) => {
  const tone = /active|on file|completed|clear/i.test(s || '') ? 'bg-emerald-50 text-emerald-700'
    : /pending|verification|upcoming/i.test(s || '') ? 'bg-amber-50 text-amber-700'
    : /inactive|ended|missing/i.test(s || '') ? 'bg-slate-100 text-slate-500' : 'bg-blue-50 text-blue-700';
  return <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${tone}`}>{s || '—'}</span>;
};

const Section = ({ icon: Icon, title, count, children, defaultOpen = true, testid }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-2xl border border-slate-200 bg-white" data-testid={testid}>
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between px-5 py-3.5 text-left">
        <span className="flex items-center gap-2.5 text-sm font-bold text-[#0E2A47]">
          <Icon size={17} className="text-[#1763B6]" /> {title}
          {count != null && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500">{count}</span>}
        </span>
        {open ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />}
      </button>
      {open && <div className="border-t border-slate-100 px-5 py-4">{children}</div>}
    </div>
  );
};

const hum = (s) => (s ? String(s).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : s);

const KV = ({ k, v }) => (
  <div className="flex items-center justify-between gap-4 py-1 text-sm">
    <span className="text-slate-500">{k}</span><span className="text-right font-medium text-[#0E2A47]">{v || '—'}</span>
  </div>
);

const PropTone = { Outstanding: 'bg-amber-100 text-amber-800', 'Partially Returned': 'bg-blue-100 text-blue-800', Returned: 'bg-emerald-100 text-emerald-800', 'Lost / Not Returned': 'bg-rose-100 text-rose-700' };
const LocationPropertyRegister = ({ locationId, token }) => {
  const [d, setD] = useState(null);
  useEffect(() => {
    axios.get(`${API}/property-register/location/${locationId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setD(r.data)).catch(() => setD({ items: [], summary: {} }));
  }, [locationId, token]);
  const items = d?.items || [];
  const s = d?.summary || {};
  return (
    <Section icon={Boxes} title="Property, Keys & Access at this Location" count={items.length} testid="slp-property-register" defaultOpen={false}>
      {!d ? <p className="flex items-center gap-1.5 text-xs text-slate-400"><Loader2 size={13} className="animate-spin" />Loading…</p>
        : items.length === 0 ? <p className="text-xs text-slate-400">No property, keys or access items issued for this location.</p>
        : <>
          {(s.outstanding_items || 0) > 0 && <div className="mb-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-[11px] font-semibold text-amber-800" data-testid="slp-prop-outstanding">{s.outstanding_items} outstanding item(s) across {s.people || 0} personnel.</div>}
          <div className="space-y-1.5">{items.map((it) => (
            <div key={it.id} className="flex items-center justify-between border-t border-slate-100 py-1.5 text-sm first:border-0">
              <span className="min-w-0 truncate"><b className="text-[#0E2A47]">{it.category}</b> · {it.item_type} <span className="text-slate-400">— {it.personnel_name}</span></span>
              <span className={`ml-2 shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${PropTone[it.return_status] || 'bg-slate-100 text-slate-500'}`}>{it.return_status}</span>
            </div>))}</div>
        </>}
    </Section>
  );
};


const CStatus = ({ s }) => {
  const map = {
    verified: ['Verified', 'bg-emerald-50 text-emerald-700'],
    pending_verification: ['Pending Verification', 'bg-amber-50 text-amber-700'],
    returned: ['Returned', 'bg-sky-50 text-sky-700'],
    rejected: ['Not Accepted', 'bg-rose-50 text-rose-700'],
  };
  const [lbl, cls] = map[s] || ['—', 'bg-slate-100 text-slate-500'];
  return <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${cls}`}>{lbl}</span>;
};

// Stage 3 — field contributions layer (verified + Operations verification queue).
const ContribCard = ({ c, review, acting, setActing, reason, setReason, busy, decide, token }) => (
    <div className="rounded-xl border border-slate-100 bg-white p-3" data-testid={`slp-contrib-${c.id}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="truncate text-sm font-bold text-[#0E2A47]">{c.category_label}{c.kind === 'correction' ? ' · Correction' : ''}</span>
        <CStatus s={c.status} />
      </div>
      <div className="mt-0.5 text-[11px] text-slate-400">{c.contributor_name} · {fmt(c.submitted_at)}{c.verified_by_name ? ` · verified by ${c.verified_by_name}` : ''}</div>
      {c.kind === 'correction' ? (
        <div className="mt-2 rounded-lg bg-slate-50 p-2 text-xs">
          <div className="text-slate-500">On file: {c.current_value || c.target_field}</div>
          <div className="font-semibold text-[#0E2A47]">Proposed: {c.proposed_value}</div>
          {c.note && <div className="mt-1 text-slate-600">{c.note}</div>}
        </div>
      ) : c.note && <p className="mt-1.5 text-sm text-slate-700">{c.note}</p>}
      {(c.status === 'rejected' || c.status === 'returned') && c.verification_note && (
        <div className="mt-2 rounded-lg bg-amber-50 p-2 text-xs text-amber-800" data-testid={`slp-contrib-decision-note-${c.id}`}>
          <span className="font-semibold">Operations {c.status === 'rejected' ? 'reason' : 'note'}:</span> {c.verification_note}
        </div>
      )}
      {c.photos?.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {c.photos.map((p, i) => <img key={i} src={`${API}${p.url}&auth=${token}`} alt="" className="h-14 w-14 rounded border border-slate-200 object-cover" />)}
        </div>
      )}
      {review && (
        <div className="mt-2.5 border-t border-slate-100 pt-2.5">
          {acting?.id === c.id ? (
            <div className="space-y-2">
              {acting.mode !== 'verify' && (
                <textarea data-testid={`slp-contrib-reason-${c.id}`} value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
                  placeholder="Reason (required)" className="w-full resize-none rounded-lg border border-slate-300 px-2.5 py-2 text-sm" />
              )}
              <div className="flex gap-2">
                <button data-testid={`slp-contrib-confirm-${c.id}`} disabled={busy} onClick={() => decide(c.id, acting.mode)}
                  className="rounded-lg bg-[#0E2A47] px-3 py-1.5 text-xs font-bold text-white disabled:opacity-60">Confirm</button>
                <button onClick={() => { setActing(null); setReason(''); }} className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600">Cancel</button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              <button data-testid={`slp-contrib-verify-${c.id}`} onClick={() => setActing({ id: c.id, mode: 'verify' })}
                className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-emerald-700"><Check size={13} /> Verify</button>
              <button data-testid={`slp-contrib-return-${c.id}`} onClick={() => setActing({ id: c.id, mode: 'return' })}
                className="inline-flex items-center gap-1 rounded-lg border border-sky-300 px-3 py-1.5 text-xs font-semibold text-sky-700 hover:bg-sky-50"><CornerUpLeft size={13} /> Return</button>
              <button data-testid={`slp-contrib-reject-${c.id}`} onClick={() => setActing({ id: c.id, mode: 'reject' })}
                className="inline-flex items-center gap-1 rounded-lg border border-rose-300 px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-50"><Ban size={13} /> Reject</button>
            </div>
          )}
        </div>
      )}
    </div>
);

const FieldContributions = ({ locationId, token }) => {
  const [state, setState] = useState({ loading: true, canVerify: false, items: [], pending: 0 });
  const [acting, setActing] = useState(null); // {id, mode}
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const H = { headers: { Authorization: `Bearer ${token}` } };

  const load = () => axios.get(`${API}/location-contributions/by-location/${locationId}?status=all`, H)
    .then((r) => setState({ loading: false, canVerify: r.data.can_verify, items: r.data.items, pending: r.data.pending_count }))
    .catch(() => setState({ loading: false, canVerify: false, items: [], pending: 0 }));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [locationId]);

  const decide = async (id, mode) => {
    if ((mode === 'reject' || mode === 'return') && !reason.trim()) { toast.error('A reason is required'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/location-contributions/${id}/${mode}`, { note: reason }, H);
      toast.success(mode === 'verify' ? 'Verified' : mode === 'reject' ? 'Marked not accepted' : 'Returned for info');
      setActing(null); setReason(''); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Action failed'); } finally { setBusy(false); }
  };

  const verified = state.items.filter((i) => i.status === 'verified');
  const open = state.items.filter((i) => i.status === 'pending_verification' || i.status === 'returned');
  const count = state.canVerify ? open.length + verified.length : verified.length;
  const cardProps = { acting, setActing, reason, setReason, busy, decide, token };

  if (state.loading) {
    return (
      <Section key="fc-loading" icon={MessageSquarePlus} title="Field Updates & Verification" testid="slp-field-contributions" defaultOpen={false}>
        <div className="py-3 text-slate-400"><Loader2 className="inline animate-spin" size={16} /></div>
      </Section>
    );
  }

  return (
    <Section key="fc-loaded" icon={MessageSquarePlus} title="Field Updates & Verification" count={count} testid="slp-field-contributions" defaultOpen={open.length > 0 || verified.length > 0}>
        <div className="space-y-4">
          {state.canVerify && (
            <div>
              <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-amber-600"><Clock size={13} /> Awaiting Verification ({open.length})</div>
              {open.length === 0 ? <p className="text-xs text-slate-400">No field updates awaiting verification.</p>
                : <div className="grid gap-2 sm:grid-cols-2" data-testid="slp-contrib-queue">{open.map((c) => <ContribCard key={c.id} c={c} review {...cardProps} />)}</div>}
            </div>
          )}
          <div>
            <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-emerald-600"><ShieldCheck size={13} /> Verified Field Layer ({verified.length})</div>
            {verified.length === 0 ? <p className="text-xs text-slate-400">No verified field contributions yet. Verified items appear here as a read-only layer and never overwrite the Building Intelligence record.</p>
              : <div className="grid gap-2 sm:grid-cols-2" data-testid="slp-contrib-verified">{verified.map((c) => <ContribCard key={c.id} c={c} {...cardProps} />)}</div>}
          </div>
        </div>
    </Section>
  );
};

const LocationReportModal = ({ locationId, token, onClose }) => {
  const H = { headers: { Authorization: `Bearer ${token}` } };
  const [manifest, setManifest] = useState(null);
  const [sel, setSel] = useState({});
  const [fmt, setFmt] = useState('pdf');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/location-reports/${locationId}/manifest`, H)
      .then((r) => {
        setManifest(r.data);
        const init = {};
        (r.data.sections || []).forEach((s) => { init[s.key] = !s.confidential; });
        init.identity = true;
        setSel(init);
      })
      .catch(() => { toast.error('Could not load report options'); onClose(); });
  }, [locationId]);

  const toggle = (k) => { if (k === 'identity') return; setSel((p) => ({ ...p, [k]: !p[k] })); };
  const allOn = manifest && manifest.sections.every((s) => sel[s.key]);
  const setAll = (v) => {
    const next = {};
    (manifest.sections || []).forEach((s) => { next[s.key] = v; });
    next.identity = true;
    setSel(next);
  };

  const generate = async () => {
    const keys = Object.keys(sel).filter((k) => sel[k]);
    if (!keys.length) { toast.error('Select at least one section'); return; }
    setBusy(true);
    try {
      const res = await axios.get(
        `${API}/location-reports/${locationId}?format=${fmt}&sections=${keys.join(',')}`,
        { ...H, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      const ext = fmt === 'xlsx' ? 'xlsx' : 'pdf';
      a.download = `TFS_Location_Report_${(manifest?.location_name || 'location').replace(/\s+/g, '_')}.${ext}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${ext.toUpperCase()} report downloaded`);
      onClose();
    } catch (e) {
      toast.error('Report generation failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-900/50 p-4" data-testid="slp-report-modal">
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3.5">
          <div className="flex items-center gap-2 text-sm font-bold text-[#0E2A47]"><FileText size={16} /> Generate Location Report</div>
          <button onClick={onClose} data-testid="slp-report-close" className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>
        {!manifest ? (
          <div className="flex h-40 items-center justify-center text-slate-400"><Loader2 className="mr-2 animate-spin" size={18} /> Loading…</div>
        ) : (
          <div className="max-h-[70vh] overflow-y-auto p-5">
            <div className="mb-3 flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wide text-slate-500">Sections to include</span>
              <button onClick={() => setAll(!allOn)} data-testid="slp-report-toggle-all" className="text-xs font-semibold text-[#1763B6]">{allOn ? 'Clear all' : 'Select all'}</button>
            </div>
            <div className="space-y-1.5">
              {manifest.sections.map((s) => (
                <label key={s.key} data-testid={`slp-report-section-${s.key}`}
                  className={`flex cursor-pointer items-center justify-between rounded-lg border px-3 py-2 text-sm ${sel[s.key] ? 'border-[#0E2A47]/30 bg-[#0E2A47]/[0.03]' : 'border-slate-200'} ${s.key === 'identity' ? 'opacity-70' : ''}`}>
                  <span className="flex items-center gap-2.5">
                    <input type="checkbox" checked={!!sel[s.key]} disabled={s.key === 'identity'} onChange={() => toggle(s.key)} data-testid={`slp-report-check-${s.key}`} className="h-4 w-4 accent-[#0E2A47]" />
                    <span className="font-medium text-slate-700">{s.label}</span>
                    {s.confidential && <span className="rounded bg-rose-50 px-1.5 py-0.5 text-[10px] font-bold text-rose-700">CONFIDENTIAL</span>}
                    {manifest.building_intelligence_demo && s.key === 'building_intelligence' && <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-bold text-amber-700">NOT VERIFIED</span>}
                  </span>
                  <span className="text-xs text-slate-400">{s.count != null ? s.count : ''}</span>
                </label>
              ))}
            </div>
            <div className="mt-4">
              <span className="text-xs font-bold uppercase tracking-wide text-slate-500">Format</span>
              <div className="mt-1.5 flex gap-2">
                {[['pdf', 'PDF'], ['xlsx', 'Excel']].map(([v, lab]) => (
                  <button key={v} onClick={() => setFmt(v)} data-testid={`slp-report-fmt-${v}`}
                    className={`flex-1 rounded-lg border px-3 py-2 text-sm font-semibold ${fmt === v ? 'border-[#0E2A47] bg-[#0E2A47] text-white' : 'border-slate-200 text-slate-600'}`}>{lab}</button>
                ))}
              </div>
            </div>
          </div>
        )}
        <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-3.5">
          <button onClick={onClose} className="rounded-lg border border-slate-300 px-3.5 py-1.5 text-xs font-semibold text-slate-700">Cancel</button>
          <button onClick={generate} disabled={busy || !manifest} data-testid="slp-report-generate" className="inline-flex items-center gap-1.5 rounded-lg bg-[#0E2A47] px-4 py-1.5 text-xs font-semibold text-white hover:bg-[#15375e] disabled:opacity-60">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} {busy ? 'Generating…' : 'Download'}
          </button>
        </div>
      </div>
    </div>
  );
};

export const ServiceLocationProfile = ({ locationId, onClose }) => {
  const { token } = useContext(AuthContext);
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const H = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    setLoading(true); setErr(false);
    axios.get(`${API}/service-locations/${locationId}/profile`, H)
      .then((r) => setD(r.data)).catch(() => { setErr(true); toast.error('Could not load the location profile'); })
      .finally(() => setLoading(false));
  }, [locationId]);

  return (
    <div className="fixed inset-0 z-[70] overflow-y-auto bg-slate-50" data-testid="service-location-profile">
      <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white/95 px-5 py-3 backdrop-blur">
        <button onClick={onClose} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1763B6]" data-testid="slp-close">
          <X size={16} /> Close
        </button>
        <div className="flex gap-2">
          <button onClick={() => window.print()} data-testid="slp-print" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"><Printer size={14} /> Print</button>
          <button onClick={() => setReportOpen(true)} data-testid="slp-report" className="inline-flex items-center gap-1.5 rounded-lg bg-[#0E2A47] px-3.5 py-1.5 text-xs font-semibold text-white hover:bg-[#15375e]"><FileText size={14} /> Generate Report</button>
        </div>
      </div>

      {reportOpen && <LocationReportModal locationId={locationId} token={token} onClose={() => setReportOpen(false)} />}

      {err ? (
        <div className="flex h-[70vh] flex-col items-center justify-center gap-2 text-slate-400">
          <AlertTriangle /> <span className="text-sm">The location profile could not be loaded.</span>
          <button onClick={onClose} className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold">Close</button>
        </div>
      ) : loading || !d ? (
        <div className="flex h-[70vh] items-center justify-center text-slate-400"><Loader2 className="mr-2 animate-spin" /> Loading profile…</div>
      ) : (
        <div className="mx-auto max-w-6xl space-y-5 p-5">
          {/* Header */}
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
            <div className="flex flex-col gap-4 p-5 sm:flex-row">
              <div className="h-40 w-full shrink-0 overflow-hidden rounded-xl bg-slate-100 sm:w-64" data-testid="slp-photo">
                {d.header.image_url
                  ? <img src={`${API}/service-locations/${locationId}/image?variant=card&auth=${token}`} alt={d.header.location_name} className="h-full w-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                  : <div className="flex h-full items-center justify-center text-slate-300"><Building2 size={48} /></div>}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="text-2xl font-extrabold text-[#0E2A47]" data-testid="slp-name">{d.header.location_name}</h1>
                  <Pill s={d.header.status} />
                </div>
                <div className="mt-1 text-sm font-semibold text-[#1763B6]">{d.header.customer_name}</div>
                <div className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1 text-sm sm:grid-cols-3">
                  <KV k="Account #" v={d.header.account_number} />
                  <KV k="Location Code" v={d.header.location_code} />
                  <KV k="Type" v={d.header.location_type} />
                </div>
                <div className="mt-1 flex items-start gap-1.5 text-sm text-slate-600"><MapPin size={15} className="mt-0.5 text-[#1763B6]" />{d.header.address || '—'}</div>
              </div>
            </div>
          </div>

          <div className="grid gap-5 lg:grid-cols-2">
            <Section icon={Building2} title="Property Identity & Service Specifications" testid="slp-identity">
              <KV k="Total sq ft" v={d.specifications.total_sq_ft ? d.specifications.total_sq_ft.toLocaleString() : null} />
              <KV k="Carpet sq ft" v={d.specifications.carpet_sq_ft ? d.specifications.carpet_sq_ft.toLocaleString() : null} />
              <KV k="Hard floor sq ft" v={d.specifications.hard_floor_sq_ft ? d.specifications.hard_floor_sq_ft.toLocaleString() : null} />
              <KV k="Floors" v={d.specifications.num_floors} />
              <KV k="Service frequency" v={d.specifications.service_frequency} />
              <KV k="Service days" v={(d.specifications.service_days || []).join(', ')} />
            </Section>

            <Section icon={Users} title="Management & Responsibilities" testid="slp-management">
              <KV k="Site contact" v={d.management.contact_name} />
              <KV k="Contact number" v={d.management.contact_number} />
              <KV k="Region" v={d.management.region} />
            </Section>
          </div>

          <Section icon={Users} title="Assigned Personnel" count={d.assigned_personnel.length} testid="slp-assigned">
            {d.assigned_personnel.length === 0 ? <p className="text-xs text-slate-400">No personnel currently assigned to this location.</p>
              : <div className="grid gap-2 sm:grid-cols-2">
                {d.assigned_personnel.map((p) => (
                  <div key={p.assignment_id} className="flex items-center gap-3 rounded-xl border border-slate-100 p-2.5" data-testid={`slp-person-${p.personnel_id}`}>
                    {p.photo_url ? <img src={`${API}/personnel/${p.personnel_id}/photo?variant=thumb&auth=${token}`} alt="" className="h-10 w-10 rounded-full object-cover" onError={(e) => { e.target.style.visibility = 'hidden'; }} /> : <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-400"><Users size={16} /></div>}
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold text-[#0E2A47]">{p.name} <span className="text-[11px] font-normal text-slate-400">#{p.personnel_number}</span></div>
                      <div className="truncate text-[11px] text-slate-500">{[p.role, p.classification].filter(Boolean).join(' · ')} · Eff {fmt(p.effective_start_date)}</div>
                      {p.reporting_manager && <div className="truncate text-[10px] text-slate-400">Reports to {p.reporting_manager}</div>}
                    </div>
                  </div>))}
              </div>}
          </Section>

          <div className="grid gap-5 lg:grid-cols-2">
            <Section icon={ShieldCheck} title="Building Intelligence" testid="slp-building-intel" defaultOpen={false}>
              <div className="mb-2"><Pill s={d.building_intelligence_status} /></div>
              {d.building_intelligence ? (
                <div className="space-y-1 text-sm">
                  <KV k="Profile #" v={d.building_intelligence.profile_number} />
                  <KV k="Industry type" v={hum(d.building_intelligence.industry_type)} />
                  <KV k="Property setting" v={hum(d.building_intelligence.property_setting)} />
                  <KV k="Square footage" v={(() => { const s = d.building_intelligence.data?.summary || {}; const v = s.square_footage || s.total_sq_ft || d.building_intelligence.total_sq_ft; return v ? Number(v).toLocaleString() : null; })()} />
                  <p className="pt-1 text-[11px] text-slate-400">Authoritative building characteristics from the Operations Building Intelligence record. Verified field contributions appear below.</p>
                </div>
              ) : <p className="text-xs text-slate-400">No Building Intelligence record on file yet. Field contributions (Team) and Operations verification are captured below.</p>}
            </Section>

            <Section icon={ClipboardList} title="Inspections & Assessments" count={d.inspections.length} testid="slp-inspections" defaultOpen={false}>
              {d.inspections.length === 0 ? <p className="text-xs text-slate-400">No inspections recorded for this location.</p>
                : <div className="space-y-1.5">{d.inspections.map((i, k) => (
                  <div key={k} className="flex items-center justify-between border-t border-slate-100 py-1.5 text-sm first:border-0">
                    <span className="truncate">{i.type}{i.inspector ? ` · ${i.inspector}` : ''}</span>
                    <span className="flex items-center gap-2 whitespace-nowrap text-[11px] text-slate-400">{fmt(i.date)} <Pill s={i.status} /></span>
                  </div>))}</div>}
            </Section>

            <Section icon={Wrench} title="Work Orders" count={d.work_orders.length} testid="slp-work-orders" defaultOpen={false}>
              {d.work_orders.length === 0 ? <p className="text-xs text-slate-400">No work orders linked to this location.</p>
                : <div className="space-y-1.5">{d.work_orders.map((w, k) => (
                  <div key={k} className="flex items-center justify-between border-t border-slate-100 py-1.5 text-sm first:border-0">
                    <span className="truncate">{w.title || w.wo_number || w.description || 'Work order'}</span><Pill s={w.status} />
                  </div>))}</div>}
            </Section>

            <Section icon={AlertTriangle} title="Incidents & Corrective Actions" count={d.corrective_actions.length} testid="slp-corrective" defaultOpen={false}>
              {d.corrective_actions.length === 0 ? <p className="text-xs text-slate-400">No corrective actions recorded.</p>
                : <div className="space-y-1.5">{d.corrective_actions.map((c, k) => (
                  <div key={k} className="flex items-center justify-between border-t border-slate-100 py-1.5 text-sm first:border-0">
                    <span className="truncate">{c.title || c.description || c.issue || 'Corrective action'}</span><Pill s={c.status} />
                  </div>))}</div>}
            </Section>
          </div>

          <FieldContributions locationId={locationId} token={token} />

          <LocationPropertyRegister locationId={locationId} token={token} />

          <Section icon={History} title="Assignment History" count={d.assignment_history.length} testid="slp-assignment-history" defaultOpen={false}>
            {d.assignment_history.length === 0 ? <p className="text-xs text-slate-400">No assignment history.</p>
              : <div className="overflow-auto">
                <table className="min-w-full text-sm">
                  <thead><tr className="border-b text-left text-[11px] uppercase text-slate-400"><th className="py-1.5 pr-3">Personnel</th><th className="py-1.5 pr-3">Role</th><th className="py-1.5 pr-3">Effective</th><th className="py-1.5 pr-3">Ended</th><th className="py-1.5">Status</th></tr></thead>
                  <tbody>{d.assignment_history.map((p) => (
                    <tr key={p.assignment_id} className="border-b border-slate-50">
                      <td className="py-1.5 pr-3 font-medium text-[#0E2A47]">{p.name} <span className="text-[11px] text-slate-400">#{p.personnel_number}</span></td>
                      <td className="py-1.5 pr-3">{p.role || '—'}</td>
                      <td className="py-1.5 pr-3">{fmt(p.effective_start_date)}</td>
                      <td className="py-1.5 pr-3">{p.effective_end_date ? fmt(p.effective_end_date) : '—'}</td>
                      <td className="py-1.5"><Pill s={p.is_active ? 'Active' : 'Ended'} /></td>
                    </tr>))}</tbody>
                </table>
              </div>}
          </Section>

          <div className="pb-10 text-center text-[11px] text-slate-400">
            Grounds &amp; Landscaping, Security &amp; Access, Equipment &amp; On-Site Inventory and Emergency Preparedness are captured as Team field contributions above and in Building Intelligence. Use <span className="font-semibold text-[#0E2A47]">Generate Report</span> for a selectable PDF or Excel export.
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceLocationProfile;
